import { Group } from '~/components/Shared/Filters_DEPRECATED/types';
import { FieldId } from '~/evifields/src/types/FieldId';

/** Anything with an evisearch filter id and a section label. */
export type Orderable = { id: FieldId; section?: string };

const PILOT_FIELD_ID = /^DYN_(\d+)$/;

type Slot = { groupIndex: number; fieldIndex: number };

/**
 * Unified Fields filter id (a bare UUID) → the Pilot field id it was projected
 * from, so both evisearch id spellings join to the same `fieldIds` entry.
 */
export type PilotIdByUfFieldId = Map<string, FieldId>;

/** 4-tuple: [groupSlot, tier, position, sourceIndex] — see the module's spec for the ordering rules. */
export type SortTuple = [number, number, number, number];

export const resolvePilotFieldId = (
  id: FieldId,
  pilotIdByUfFieldId: PilotIdByUfFieldId,
): string | undefined =>
  pilotIdByUfFieldId.get(String(id)) ?? PILOT_FIELD_ID.exec(String(id))?.[1];

export const buildPilotIdByUfFieldId = (
  fields?: Record<string, { id: FieldId; ufFieldId?: string | null }>,
): PilotIdByUfFieldId => {
  const map: PilotIdByUfFieldId = new Map();
  Object.values(fields ?? {}).forEach((field) => {
    if (field.ufFieldId) {
      map.set(field.ufFieldId, field.id);
    }
  });
  return map;
};

const buildFieldIndex = (fieldGroups: Group[]) => {
  const byFieldId = new Map<string, Slot>();
  const byGroupId = new Map<string, number>();

  fieldGroups.forEach((group, groupIndex) => {
    if (!byGroupId.has(group.id)) {
      byGroupId.set(group.id, groupIndex);
    }
    group.fieldIds.forEach((fieldId, fieldIndex) => {
      if (!byFieldId.has(fieldId)) {
        byFieldId.set(fieldId, { groupIndex, fieldIndex });
      }
    });
  });

  return { byFieldId, byGroupId };
};

/**
 * First-appearance rank of every section string that doesn't join to a Pilot
 * field id and doesn't name a known Pilot group — computed in its own pass so
 * the tail order depends only on input order, never on Map/object iteration
 * order.
 */
const buildTailRanks = (
  items: Orderable[],
  byFieldId: Map<string, Slot>,
  byGroupId: Map<string, number>,
  pilotIdByUfFieldId: PilotIdByUfFieldId,
) => {
  const tailRanks = new Map<string, number>();

  items.forEach((item) => {
    const fieldId = resolvePilotFieldId(item.id, pilotIdByUfFieldId);
    if (fieldId !== undefined && byFieldId.has(fieldId)) return;

    const section = item.section ?? '';
    if (byGroupId.has(section)) return;

    if (!tailRanks.has(section)) {
      tailRanks.set(section, tailRanks.size);
    }
  });

  return tailRanks;
};

export const compareTuples = (a: SortTuple, b: SortTuple): number => {
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
};

/**
 * Reorders a flat list of evisearch filters (or anything id/section-shaped)
 * into the admin's Pilot field order, joining on the numeric id embedded in
 * evisearch's `DYN_<id>` filter ids — or, for a Unified Fields client whose
 * filter ids are bare UUIDs, on `pilotIdByUfFieldId`. Never groups, drops, or
 * invents items — see the field-order spec for the full contract.
 */
export const orderByPilotFields = <T extends Orderable>(
  items: T[],
  fieldGroups: Group[],
  pilotIdByUfFieldId: PilotIdByUfFieldId = new Map(),
): T[] => {
  if (!fieldGroups || fieldGroups.length === 0) return items;

  const { byFieldId, byGroupId } = buildFieldIndex(fieldGroups);
  const tailRanks = buildTailRanks(
    items,
    byFieldId,
    byGroupId,
    pilotIdByUfFieldId,
  );
  const groupCount = fieldGroups.length;

  const decorated = items.map((item, sourceIndex) => {
    const fieldId = resolvePilotFieldId(item.id, pilotIdByUfFieldId);
    const slot = fieldId !== undefined ? byFieldId.get(fieldId) : undefined;

    if (slot) {
      const section = fieldGroups[slot.groupIndex].id;
      const next = item.section === section ? item : { ...item, section };
      const tuple: SortTuple = [
        slot.groupIndex,
        0,
        slot.fieldIndex,
        sourceIndex,
      ];
      return { item: next, tuple };
    }

    const section = item.section ?? '';
    const knownGroupIndex = byGroupId.get(section);
    const groupSlot =
      knownGroupIndex ?? groupCount + (tailRanks.get(section) ?? 0);
    const tuple: SortTuple = [groupSlot, 1, 0, sourceIndex];
    return { item, tuple };
  });

  decorated.sort((a, b) => compareTuples(a.tuple, b.tuple));

  return decorated.map((decoratedItem) => decoratedItem.item);
};
